/**
 * useSSE — Server-Sent Events 实时通知 Hook
 * - 连接后端 SSE 端点 /v1/notifications/stream
 * - 接收到新通知时自动追加到 NotificationStore
 * - 断线后 3 秒自动重连
 * - 组件卸载时自动断开连接
 *
 * 使用方式：
 *   const { connect, disconnect } = useSSE();
 *   useEffect(() => { connect(); return disconnect; }, []);
 */
import {useEffect, useRef, useCallback} from 'react'
import {useNotificationStore} from '@/stores/useNotificationStore'
import type {Notification} from '@/types'
import {appConfig} from '@/config'

export function useSSE() {
  const eventSourceRef = useRef<EventSource | null>(null)
  const reconnectTimerRef = useRef<number | null>(null)
  const connectingRef = useRef(false)
  const shouldReconnectRef = useRef(false)
  const addNotification = useNotificationStore((s) => s.addNotification)
  const refreshUnreadCount = useNotificationStore((s) => s.refreshUnreadCount)

  /** 建立 SSE 连接 */
  const connect = useCallback(
    async (options: {skipUnreadPreflight?: boolean} = {}) => {
      shouldReconnectRef.current = true
      if (eventSourceRef.current || connectingRef.current) return // 防止重复连接
      connectingRef.current = true

      if (!options.skipUnreadPreflight) {
        try {
          // EventSource 不是 axios 请求，不会自动走 refresh token 拦截器。
          // 所以真正打开 SSE 前，先用一个普通接口做鉴权预检：
          // - accessToken 过期时，axios 会自动 refresh 并重试；
          // - refresh 失败时，这里直接停止，不再创建 EventSource，也就不会 401 无限重连。
          await refreshUnreadCount()
        } catch {
          shouldReconnectRef.current = false
          connectingRef.current = false
          return
        }
      }

      const es = new EventSource(appConfig.sse.notificationsURL, {
        withCredentials: true
      })

      // 接收到消息时解析并追加到 store
      es.onmessage = (event) => {
        try {
          const notification: Notification = JSON.parse(event.data)
          addNotification(notification)
        } catch {
          // 忽略解析错误（如心跳消息）
        }
      }

      // 连接断开时自动重连
      es.onerror = () => {
        es.close()
        eventSourceRef.current = null
        connectingRef.current = false

        if (!shouldReconnectRef.current) return

        if (reconnectTimerRef.current) {
          window.clearTimeout(reconnectTimerRef.current)
        }

        reconnectTimerRef.current = window.setTimeout(() => {
          reconnectTimerRef.current = null
          connect()
        }, appConfig.sse.reconnectDelayMs)
      }

      eventSourceRef.current = es
      connectingRef.current = false
    },
    [addNotification, refreshUnreadCount]
  )

  /** 主动断开 SSE 连接 */
  const disconnect = useCallback(() => {
    shouldReconnectRef.current = false
    connectingRef.current = false

    if (reconnectTimerRef.current) {
      window.clearTimeout(reconnectTimerRef.current)
      reconnectTimerRef.current = null
    }

    if (eventSourceRef.current) {
      eventSourceRef.current.close()
      eventSourceRef.current = null
    }
  }, [])

  // 组件卸载时清理连接
  useEffect(() => {
    return () => disconnect()
  }, [disconnect])

  return {connect, disconnect}
}
